import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Download,
  Search,
  Check,
  FileText,
  Video,
  Mail,
  Image as ImageIcon
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Assets = () => {
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchAssets();
  }, [filter]);

  const fetchAssets = async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.append("asset_type", filter);
      if (search) params.append("search", search);
      
      const response = await axios.get(`${API}/assets?${params.toString()}`, { 
        withCredentials: true 
      });
      setAssets(response.data);
    } catch (error) {
      console.error("Assets fetch error:", error);
      toast.error("Failed to load assets");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAssets();
  };

  const handleDownload = async (asset) => {
    setDownloading(asset.asset_id);
    try {
      const response = await axios.post(
        `${API}/assets/${asset.asset_id}/download`, 
        {},
        { withCredentials: true }
      );
      
      // Open file URL in new tab (simulated download)
      if (response.data.file_url && response.data.file_url !== "#") {
        window.open(response.data.file_url, "_blank");
      }
      
      toast.success(`Downloaded: ${asset.asset_name}`);
      
      // Update local state to show downloaded
      setAssets(prev => prev.map(a => 
        a.asset_id === asset.asset_id ? { ...a, downloaded: true } : a
      ));
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download asset");
    } finally {
      setDownloading(null);
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      social: ImageIcon,
      email: Mail,
      video: Video
    };
    return icons[type] || FileText;
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      social: "bg-purple-50 text-purple-700 border-purple-200",
      email: "bg-blue-50 text-blue-700 border-blue-200",
      video: "bg-rose-50 text-rose-700 border-rose-200"
    };
    return colors[type] || "bg-slate-50 text-slate-700 border-slate-200";
  };

  const filterButtons = [
    { value: "all", label: "All" },
    { value: "social", label: "Social Media" },
    { value: "email", label: "Email Templates" },
    { value: "video", label: "Video Scripts" }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex gap-4 flex-wrap">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-10 w-28" />
          ))}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Card key={i} className="bg-white border-slate-100">
              <Skeleton className="h-40 w-full" />
              <CardContent className="p-4">
                <Skeleton className="h-4 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex gap-2 flex-wrap">
          {filterButtons.map((btn) => (
            <Button
              key={btn.value}
              variant={filter === btn.value ? "default" : "outline"}
              onClick={() => setFilter(btn.value)}
              data-testid={`filter-${btn.value}`}
              className={filter === btn.value ? "gradient-btn text-white" : ""}
            >
              {btn.label}
            </Button>
          ))}
        </div>
        
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search assets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 w-64"
              data-testid="search-input"
            />
          </div>
          <Button type="submit" variant="outline" data-testid="search-btn">
            Search
          </Button>
        </form>
      </div>

      {/* Assets Grid */}
      {assets.length === 0 ? (
        <Card className="bg-white border-slate-100">
          <CardContent className="py-16 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No assets found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {assets.map((asset) => {
            const TypeIcon = getTypeIcon(asset.asset_type);
            
            return (
              <Card 
                key={asset.asset_id} 
                className="bg-white border-slate-100 shadow-sm card-hover overflow-hidden group"
                data-testid={`asset-card-${asset.asset_id}`}
              >
                {/* Thumbnail */}
                <div className="relative h-40 bg-slate-100 overflow-hidden">
                  {asset.thumbnail_url ? (
                    <img 
                      src={asset.thumbnail_url} 
                      alt={asset.asset_name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <TypeIcon className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  
                  {/* Downloaded badge */}
                  {asset.downloaded && (
                    <div className="absolute top-2 right-2">
                      <Badge className="bg-emerald-500 text-white border-0">
                        <Check className="w-3 h-3 mr-1" />
                        Downloaded
                      </Badge>
                    </div>
                  )}
                </div>
                
                {/* Content */}
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <h3 className="font-medium text-slate-900 text-sm leading-tight">
                      {asset.asset_name}
                    </h3>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Badge className={`${getTypeBadgeColor(asset.asset_type)} capitalize text-xs`}>
                      {asset.asset_type}
                    </Badge>
                    <span className="text-xs text-slate-500">
                      {asset.download_count} downloads
                    </span>
                  </div>
                  
                  <Button
                    onClick={() => handleDownload(asset)}
                    disabled={downloading === asset.asset_id}
                    className="w-full mt-4 gradient-btn text-white"
                    data-testid={`download-btn-${asset.asset_id}`}
                  >
                    {downloading === asset.asset_id ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Assets;
